import { captureException } from '@sentry/react';
import { ChevronLeft } from 'lucide-react';
import { ComponentProps, useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../../../contexts/data-context/use-data';
import { Button } from '../../../../../design-system/button/button';
import { DrawerClose, DrawerHeader } from '../../../../../design-system/drawer/drawer';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { CategoryView, VendorView } from '../../../../../types/entity-types';
import { deleteSwrFetcher } from '../../../../../utils/delete-swr-fetcher';
import { patchSwrFetcher } from '../../../../../utils/patch-swr-fetcher';
import { DuplicateVendorCard } from '../../../../transactions/components/edit-vendor-modal/duplicate-vendor-card';
import { EditVendorForm } from '../../../../transactions/components/edit-vendor-modal/edit-vendor-form';

const VENDOR_API_URL = '/api/entities/by-type/vendor';
const TRANSACTION_API_URL = '/api/entities/by-type/transaction';

type EditVendorProps = {
  vendor: VendorView | null;
  categories: CategoryView[];
  vendors: VendorView[];
  onBack: () => void;
};

export const EditVendor = ({ onBack, vendor, categories, vendors }: EditVendorProps) => {
  const [duplicateVendorInfo, setDuplicateVendorInfo] = useState<
    | {
        isDuplicate: false;
      }
    | {
        isDuplicate: true;
        duplicateVendorIds: string[];
        incrementalTransactionIds: string[];
      }
  >({ isDuplicate: false });
  const [isLoading, setIsLoading] = useState(false);
  const { mutations } = useData();
  const { trigger: triggerVendorUpdate } = useSWRMutation(VENDOR_API_URL, patchSwrFetcher);
  const { trigger: triggerTransactionUpdate } = useSWRMutation(TRANSACTION_API_URL, patchSwrFetcher);
  const { trigger: triggerVendorDelete } = useSWRMutation(VENDOR_API_URL, deleteSwrFetcher);

  if (!vendor) {
    return null;
  }

  const onEditVendor = async ({
    name,
    categoryId,
    description,
  }: Parameters<ComponentProps<typeof EditVendorForm>['onSubmit']>[0]) => {
    try {
      setIsLoading(true);

      const patchOperation = [
        {
          id: vendor.id,
          patch: [
            {
              op: 'replace',
              path: '/name',
              value: name,
            },
            {
              op: 'replace',
              path: '/categoryId',
              value: categoryId,
            },
            {
              op: 'replace',
              path: '/description',
              value: description,
            },
          ],
        },
      ];

      await triggerVendorUpdate(patchOperation);
      await mutations.handleIncrementalUpdates();
      toast.success('Vendor updated successfully');
      onBack();
    } catch (error) {
      toast.error('Failed to update vendor');
      captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onEditVendorAndMergeTransactions = async ({
    name,
    categoryId,
    description,
  }: Parameters<ComponentProps<typeof EditVendorForm>['onSubmit']>[0]) => {
    if (!duplicateVendorInfo.isDuplicate) {
      return;
    }

    setIsLoading(true);

    try {
      // We use the logo of the first duplicate vendor to merge the transactions
      const duplicateVendorLogo = vendors.find((v) => v.id === duplicateVendorInfo.duplicateVendorIds[0])?.logo;

      const patchVendorOperation = [
        {
          id: vendor.id,
          patch: [
            {
              op: 'replace',
              path: '/name',
              value: name,
            },
            {
              op: 'replace',
              path: '/categoryId',
              value: categoryId,
            },
            {
              op: 'replace',
              path: '/description',
              value: description,
            },
            {
              op: 'replace',
              path: '/logo',
              value: duplicateVendorLogo,
            },
          ],
        },
      ];

      const patchTransactionOperations = duplicateVendorInfo.incrementalTransactionIds.map((txnId) => [
        {
          id: txnId,
          patch: [
            {
              op: 'replace',
              path: '/vendorId',
              value: vendor.id,
            },
          ],
        },
      ]);

      await Promise.all([
        triggerVendorUpdate(patchVendorOperation),
        ...patchTransactionOperations.map((txn) => triggerTransactionUpdate(txn)),
      ]);

      await mutations.handleIncrementalUpdates();

      await triggerVendorDelete(duplicateVendorInfo.duplicateVendorIds);

      await mutations.handleDeleteUpdates({
        entityType: 'vendor',
        entityIds: duplicateVendorInfo.duplicateVendorIds,
      });

      toast.success('Transactions merged successfully');
      onBack();
    } catch (error) {
      toast.error('Failed to merge transactions');
      captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onCheckNewDetails = async ({
    name,
    categoryId,
    description,
  }: Parameters<ComponentProps<typeof EditVendorForm>['onSubmit']>[0]) => {
    const duplicateVendors = vendors.filter((v) => v.name === name && v.id !== vendor.id);

    if (duplicateVendors.length === 0) {
      await onEditVendor({ name, categoryId, description });
      return;
    }

    if (!duplicateVendorInfo.isDuplicate) {
      setDuplicateVendorInfo({
        isDuplicate: true,
        duplicateVendorIds: duplicateVendors.map((v) => v.id),
        incrementalTransactionIds: duplicateVendors.flatMap(
          (v) => v.transactions?.filter(Boolean).map((t) => t.id) || [],
        ),
      });
      return;
    }

    await onEditVendorAndMergeTransactions({ name, categoryId, description });
  };

  return (
    <Stack className="h-full w-full bg-white" space={5}>
      <DrawerHeader>
        <Inline space={2} align="center">
          <Button variant="secondary" size="icon" onClick={onBack} className="h-8 w-8 p-0">
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <h2 className="text-base font-medium">Edit Vendor</h2>
        </Inline>
        <DrawerClose />
      </DrawerHeader>

      {duplicateVendorInfo.isDuplicate && (
        <div className="px-5">
          <DuplicateVendorCard />
        </div>
      )}

      <div className="h-full w-full px-5">
        <EditVendorForm
          name={vendor.name ?? ''}
          category={vendor.category}
          categories={categories}
          description={vendor.description}
          onSubmit={onCheckNewDetails}
          onCancel={onBack}
          isDuplicateName={duplicateVendorInfo.isDuplicate}
          isLoading={isLoading}
        />
      </div>
    </Stack>
  );
};
